import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { memberAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import { cn } from '../lib/utils';

function MemberManagement() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [memberStats, setMemberStats] = useState(null);
  const [syncingPaceData, setSyncingPaceData] = useState(false);
  const [importingMembers, setImportingMembers] = useState(false);

  // Create member modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingMember, setCreatingMember] = useState(false);
  const [newMemberData, setNewMemberData] = useState({
    ghin: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    handicapIndex: '',
    membershipStatus: 'active',
    membershipType: 'full'
  });

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [membershipTypeFilter, setMembershipTypeFilter] = useState('');
  const [sortBy, setSortBy] = useState('lastName');
  const [sortOrder, setSortOrder] = useState('asc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMembers, setTotalMembers] = useState(0);
  const [itemsPerPage] = useState(25);

  // Load members with current filters
  const loadMembers = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder
      };

      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      if (membershipTypeFilter) params.membershipType = membershipTypeFilter;

      const response = await memberAPI.getAll(params);
      const { members: memberData, pagination } = response.data.data;

      setMembers(memberData);
      setCurrentPage(pagination.page);
      setTotalPages(pagination.pages);
      setTotalMembers(pagination.total);

    } catch (err) {
      console.error('Error loading members:', err);
      setError(err.response?.data?.error || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  // Load member statistics
  const loadMemberStats = async () => {
    try {
      const response = await memberAPI.getStats();
      setMemberStats(response.data.data);
    } catch (err) {
      console.error('Error loading member stats:', err);
    }
  };

  useEffect(() => {
    loadMembers();
  }, [currentPage, sortBy, sortOrder]);

  useEffect(() => {
    loadMemberStats();
  }, []);

  // Apply filters with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentPage === 1) {
        loadMembers();
      } else {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, statusFilter, membershipTypeFilter]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return '↕';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const handleCreateMember = () => {
    setShowCreateModal(true);
    setNewMemberData({
      ghin: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      handicapIndex: '',
      membershipStatus: 'active',
      membershipType: 'full'
    });
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreatingMember(false);
  };

  const handleNewMemberChange = (field, value) => {
    setNewMemberData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitNewMember = async () => {
    if (!newMemberData.ghin || !newMemberData.firstName || !newMemberData.lastName) {
      alert('GHIN, First Name, and Last Name are required');
      return;
    }

    try {
      setCreatingMember(true);
      const memberPayload = {
        ...newMemberData,
        handicapIndex: newMemberData.handicapIndex ? parseFloat(newMemberData.handicapIndex) : null,
        displayName: `${newMemberData.firstName} ${newMemberData.lastName}`
      };

      await memberAPI.create(memberPayload);
      alert(`Member ${memberPayload.displayName} created successfully!`);
      setShowCreateModal(false);
      loadMembers();
      loadMemberStats();
    } catch (err) {
      console.error('Error creating member:', err);
      alert(err.response?.data?.error || 'Failed to create member');
    } finally {
      setCreatingMember(false);
    }
  };

  const handleDeleteMember = async (member) => {
    if (!window.confirm(`Are you sure you want to deactivate ${member.fullName || `${member.firstName} ${member.lastName}`}?`)) {
      return;
    }

    try {
      await memberAPI.delete(member._id);
      loadMembers();
      loadMemberStats();
    } catch (err) {
      console.error('Error deleting member:', err);
      alert(err.response?.data?.error || 'Failed to delete member');
    }
  };

  const handleSyncAllPaceData = async () => {
    if (!window.confirm('This will sync pace of play data for all members. This may take several minutes. Continue?')) {
      return;
    }

    try {
      setSyncingPaceData(true);
      const response = await memberAPI.syncAllMembersPaceData();
      const result = response.data.data;

      alert(`Pace data sync completed!\nSuccessful: ${result.successful}\nFailed: ${result.failed}\nProcessed: ${result.processed}`);

      loadMembers();
      loadMemberStats();
    } catch (err) {
      console.error('Error syncing pace data:', err);
      alert(err.response?.data?.error || 'Failed to sync pace data');
    } finally {
      setSyncingPaceData(false);
    }
  };



  const handleImportMembers = async (dryRun = false) => {
    const filename = 'members.xlsx';
    const message = dryRun
      ? `Preview import from ${filename}? This will show what would be imported without making changes.`
      : `Import members from ${filename}? This will create/update member records in the database.`;

    if (!window.confirm(message)) {
      return;
    }

    try {
      setImportingMembers(true);
      const options = {
        updateExisting: true,
        dryRun
      };

      const response = await memberAPI.importFromFile(filename, options);
      const result = response.data.data;

      if (dryRun) {
        const preview = result.sampleMembers.map(m => `${m.displayName} (${m.ghin})`).join('\n');
        alert(`Import Preview:\n\nFile: ${result.fileName}\nTotal Rows: ${result.totalRows}\nValid Members: ${result.validMembers}\nErrors: ${result.errors}\n\nSample Members:\n${preview}`);
      } else {
        alert(`Member import completed!\n\nFile: ${result.fileName}\nProcessed: ${result.results.processed}\nCreated: ${result.results.created}\nUpdated: ${result.results.updated}\nSkipped: ${result.results.skipped}\nErrors: ${result.results.errors.length}`);

        loadMembers();
        loadMemberStats();
      }

    } catch (err) {
      console.error('Error importing members:', err);
      const errorMsg = err.response?.data?.error || 'Failed to import members';
      alert(`Import failed: ${errorMsg}`);
    } finally {
      setImportingMembers(false);
    }
  };

  const handleCreateWallet = async (member) => {
    const initialBalance = parseFloat(prompt(`Create wallet for ${member.firstName} ${member.lastName}?\n\nEnter initial balance (optional, default: $0.00):`)) || 0;

    if (initialBalance < 0) {
      alert('Initial balance cannot be negative');
      return;
    }

    try {
      const response = await memberAPI.createMemberWallet(member._id, initialBalance, `Initial wallet for ${member.firstName} ${member.lastName}`);
      alert(`Wallet created successfully!\nInitial balance: $${initialBalance.toFixed(2)}`);

      loadMembers();
    } catch (err) {
      console.error('Error creating wallet:', err);
      alert(err.response?.data?.error || 'Failed to create wallet');
    }
  };

  const handleSyncWallet = async (member) => {
    try {
      const response = await memberAPI.syncMemberWallet(member._id);
      const result = response.data.data;

      alert(`Wallet synced successfully!\nCurrent balance: $${result.balance.toFixed(2)}`);

      loadMembers();
    } catch (err) {
      console.error('Error syncing wallet:', err);
      alert(err.response?.data?.error || 'Failed to sync wallet');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatHandicap = (handicap) => {
    if (handicap === null || handicap === undefined) return 'N/A';
    return handicap.toFixed(1);
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'suspended': return 'destructive';
      case 'expired': return 'warning';
      case 'honorary': return 'info';
      default: return 'secondary';
    }
  };

  // Loading skeleton
  if (loading && members.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header skeleton */}
          <div className="flex justify-between items-center">
            <div className="space-y-2">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-4 w-48" />
            </div>
            <div className="flex gap-2">
              <Skeleton className="h-10 w-32" />
              <Skeleton className="h-10 w-32" />
            </div>
          </div>

          {/* Stats skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="bg-gradient-to-br from-indigo-500 to-purple-600">
                <CardContent className="p-6">
                  <Skeleton className="h-8 w-16 bg-white/20 mb-2" />
                  <Skeleton className="h-4 w-24 bg-white/20" />
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Table skeleton */}
          <Card>
            <CardContent className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Back navigation */}
        <div>
          <Link
            to="/dashboard"
            className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 transition-colors"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </Link>
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Member Management
            </h1>
            <p className="text-slate-600 mt-1">Manage club members and their information</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => handleImportMembers(true)}
              disabled={importingMembers}
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
            >
              {importingMembers ? 'Loading...' : 'Preview Import'}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleImportMembers(false)}
              disabled={importingMembers}
              className="border-amber-200 text-amber-700 hover:bg-amber-50"
            >
              {importingMembers ? 'Importing...' : 'Import Members'}
            </Button>
            <Button
              variant="secondary"
              onClick={handleSyncAllPaceData}
              disabled={syncingPaceData}
            >
              {syncingPaceData ? 'Syncing...' : 'Sync All Pace Data'}
            </Button>
            <Button onClick={handleCreateMember}>
              Add New Member
            </Button>
          </div>
        </div>

        {/* Stats cards */}
        {memberStats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="text-4xl font-bold">{memberStats.totalMembers}</div>
                <div className="text-indigo-100 mt-1">Total Members</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="text-4xl font-bold">{memberStats.activeMembers}</div>
                <div className="text-emerald-100 mt-1">Active Members</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="text-4xl font-bold">{formatHandicap(memberStats.avgHandicap)}</div>
                <div className="text-amber-100 mt-1">Avg Handicap</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search members by name, GHIN, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="expired">Expired</option>
                <option value="honorary">Honorary</option>
              </select>

              <select
                value={membershipTypeFilter}
                onChange={(e) => setMembershipTypeFilter(e.target.value)}
                className="px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
              >
                <option value="">All Types</option>
                <option value="full">Full</option>
                <option value="associate">Associate</option>
                <option value="junior">Junior</option>
                <option value="senior">Senior</option>
                <option value="corporate">Corporate</option>
              </select>
            </div>

            <div className="mt-3 text-sm text-slate-500">
              Showing {members.length} of {totalMembers} members
            </div>
          </CardContent>
        </Card>

        {/* Error message */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription className="flex items-center justify-between">
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={loadMembers}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Members table */}
        <Card className="shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
                  <th
                    onClick={() => handleSort('lastName')}
                    className="px-6 py-4 text-left text-sm font-semibold cursor-pointer hover:bg-white/10 transition-colors"
                  >
                    Name {getSortIcon('lastName')}
                  </th>
                  <th
                    onClick={() => handleSort('ghin')}
                    className="px-6 py-4 text-left text-sm font-semibold cursor-pointer hover:bg-white/10 transition-colors"
                  >
                    GHIN {getSortIcon('ghin')}
                  </th>
                  <th
                    onClick={() => handleSort('handicapIndex')}
                    className="px-6 py-4 text-left text-sm font-semibold cursor-pointer hover:bg-white/10 transition-colors"
                  >
                    Handicap {getSortIcon('handicapIndex')}
                  </th>
                  <th
                    onClick={() => handleSort('membershipStatus')}
                    className="px-6 py-4 text-left text-sm font-semibold cursor-pointer hover:bg-white/10 transition-colors"
                  >
                    Status {getSortIcon('membershipStatus')}
                  </th>
                  <th
                    onClick={() => handleSort('membershipType')}
                    className="px-6 py-4 text-left text-sm font-semibold cursor-pointer hover:bg-white/10 transition-colors"
                  >
                    Type {getSortIcon('membershipType')}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Contact</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {members.map((member) => (
                  <tr
                    key={member._id}
                    className={cn(
                      "hover:bg-slate-50 transition-colors",
                      !member.isActive && "opacity-60 bg-slate-50"
                    )}
                  >
                    <td className="px-6 py-4">
                      <Link
                        to={`/members/${member._id}`}
                        className="group"
                      >
                        <div className="font-medium text-slate-900 group-hover:text-indigo-600 transition-colors">
                          {member.lastName}, {member.firstName}
                        </div>
                        {member.nickname && (
                          <div className="text-sm text-slate-500">"{member.nickname}"</div>
                        )}
                      </Link>
                    </td>
                    <td className="px-6 py-4 font-mono text-slate-600">{member.ghin}</td>
                    <td className="px-6 py-4 font-semibold text-slate-900">
                      {formatHandicap(member.handicapIndex)}
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={getStatusBadgeVariant(member.membershipStatus)}>
                        {member.membershipStatus}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-slate-600 capitalize">
                      {member.membershipType}
                    </td>
                    <td className="px-6 py-4">
                      {member.email && (
                        <div className="text-sm text-slate-600">{member.email}</div>
                      )}
                      {member.phone && (
                        <div className="text-sm text-slate-500">{member.phone}</div>
                      )}
                    </td>
                    <td className="px-6 py-4"></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>

            <span className="text-sm text-slate-600">
              Page {currentPage} of {totalPages}
            </span>

            <Button
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        )}

        {/* Create Member Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-slate-900">Add New Member</h2>
                  <button
                    onClick={handleCloseCreateModal}
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Mode toggle */}
                <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">GHIN *</label>
                      <input
                        type="text"
                        value={newMemberData.ghin}
                        onChange={(e) => handleNewMemberChange('ghin', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Enter GHIN number"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
                        <input
                          type="text"
                          value={newMemberData.firstName}
                          onChange={(e) => handleNewMemberChange('firstName', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
                        <input
                          type="text"
                          value={newMemberData.lastName}
                          onChange={(e) => handleNewMemberChange('lastName', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={newMemberData.email}
                        onChange={(e) => handleNewMemberChange('email', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={newMemberData.phone}
                        onChange={(e) => handleNewMemberChange('phone', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Handicap Index</label>
                      <input
                        type="number"
                        step="0.1"
                        value={newMemberData.handicapIndex}
                        onChange={(e) => handleNewMemberChange('handicapIndex', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="e.g. 12.5"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                        <select
                          value={newMemberData.membershipStatus}
                          onChange={(e) => handleNewMemberChange('membershipStatus', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                        >
                          <option value="active">Active</option>
                          <option value="suspended">Suspended</option>
                          <option value="expired">Expired</option>
                          <option value="honorary">Honorary</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                        <select
                          value={newMemberData.membershipType}
                          onChange={(e) => handleNewMemberChange('membershipType', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                        >
                          <option value="full">Full</option>
                          <option value="associate">Associate</option>
                          <option value="junior">Junior</option>
                          <option value="senior">Senior</option>
                          <option value="corporate">Corporate</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button variant="outline" onClick={handleCloseCreateModal}>
                        Cancel
                      </Button>
                      <Button onClick={handleSubmitNewMember} disabled={creatingMember}>
                        {creatingMember ? 'Creating...' : 'Create Member'}
                      </Button>
                    </div>
                  </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MemberManagement;
